/**
 * DraggrManager.ts - DraggrManager - Universal drag & drop with file export
 * 
 * CHANGE HISTORY:
 * - 251021_2138 [3dbdca8] danielbeer: fix: Remove all remaining duplicate type annotations in searchr and draggr
 * - 251021_2028 [c5f5984] danielbeer: fix: Resolve additional TypeScript syntax errors
 * - 251021_2025 [cc6aae8] danielbeer: fix: Resolve TypeScript migration errors
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0509 [c090082] danielbeer: feat: complete remaining TypeScript fixes across all packages
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { nanoid } from 'nanoid';


interface DraggrManagerOptions {
  actr?: any; // Actr instance
  [key: string]: any;
}

export class DraggrManager {
  private actr: any;
  constructor(options: DraggrManagerOptions = {}) {
    // Get Actr if provided, otherwise try to get it from Dependr
    this.actr = options.actr || this._tryGetActr();
    this.options = {
      enableUndo: true,
      enableMultiSelect: true,
      enableCrossView: true,
      enableFileExport: true,
      defaultTransferMode: 'auto', // 'p2p', 'proxied', 'auto'
      ...options
    };
    
    // Active drags
    this.activeDrags = new Map();
    
    // Registered drop zones
    this.dropZones = new Map();
    
    // Source adapters
    this.adapters = new Map();
    
    // Event listeners
    this.listeners = new Set();
    
    // Current drag state
    this.currentDrag = null;
    
    // Multi-select state
    this.selectedItems = new Set();
    
    // Bind methods
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
  }
  

  /**
   * Try to get Actr from Dependr (optional)
   */
  private _tryGetActr() {
    try {
      const { getActr } = require('@opcentral/actr');
      return getActr();
    } catch {
      return null;
    }
  }

  /**
   * Register Draggr actions with Actr
   * 
   * ARCHITECTURE NOTE: Draggr uses ACTIONS-ONLY pattern
   * 
   * Why this is different from fylr/taggr:
   * 1. Ephemeral Drag State: Drag operations are temporary
   *    - Drag state exists only during drag operation
   *    - State cleared when drag ends
   *    - No persistence needed - transient UI interaction
   *    - CRUD would add unnecessary overhead
   * 
   * 2. Event-Driven: Drag & drop are DOM events
   *    - Triggered by user mouse/touch interactions
   *    - State managed by browser drag API
   *    - Ephemeral - exists only during interaction
   *    - Actions handle event-driven operations
   * 
   * 3. High-Frequency Operations: Drag events fire constantly
   *    - dragover fires on every pixel movement
   *    - Performance-critical (must be <1ms)
   *    - CRUD overhead would degrade UX
   *    - In-memory state for speed
   * 
   * Pattern: Actions-Only (No CRUD)
   * - Actions: For drag operations (setup, fetch, export)
   * - No CRUD: Drag state is ephemeral, event-driven
   * - Memory-based: State stored in Map, not database
   * 
   * Compare to other patterns:
   * - commandr: Actions-Only (event-driven, high-frequency)
   * - clippr: Actions-Only (ephemeral, OS-integrated)
   * - draggr: Actions-Only (ephemeral drag state, event-driven)
   * 
   * See: /docs/ACTR_INTEGRATION_PATTERNS.md for complete explanation
   * See: /packages/draggr/README.md \"Actr Integration\" section
   */
  private registerActions(): void {
    if (!this.actr || typeof this.actr.register !== 'function') {
      console.warn('[Draggr] Actr not available or missing register method - actions not registered');
      return;
    }
    
    // setupFileExport action
    this.actr.register('draggr:setup-file-export', {
      handler: async (params) => {
        return await this.setupFileExport(params);
      }
    });

    // fetchFileBlob action
    this.actr.register('draggr:fetch-file-blob', {
      handler: async (params) => {
        return await this.fetchFileBlob(params);
      }
    });
    
    // Note: No CRUD operations - drag state is ephemeral and event-driven.
    // State exists only during drag operation, cleared when drag ends.
  }

  /**
   * Register a source adapter
   */
  registerAdapter(type: string, adapter): void {
    this.adapters.set(type, adapter);
  }
  
  /**
   * Make an element draggable
   */
  makeDraggable(itemId, config = {}): void {
    const dragId = nanoid();
    
    const dragConfig = {
      id: dragId,
      itemId,
      type: config.type || 'item',
      data: config.data || {},
      exportable: config.exportable || false,
      source: config.source || null,
      onDragStart: config.onDragStart,
      onDragEnd: config.onDragEnd,
      onExport: config.onExport,
      ghost: config.ghost || null,
      canDrag: config.canDrag !== false
    };
    
    this.activeDrags.set(dragId, dragConfig);
    
    return {
      draggable: dragConfig.canDrag,
      'data-drag-id': dragId,
      'data-item-id': itemId,
      onDragStart: (e) => this.handleDragStart(e, dragConfig),
      onDragEnd: (e) => this.handleDragEnd(e, dragConfig)
    };
  }
  
  /**
   * Make an element a drop zone
   */
  makeDroppable(zoneId, config = {}): void {
    const dropConfig = {
      id: zoneId,
      accepts: config.accepts || ['*'], // Array of accepted types
      onDrop: config.onDrop,
      onDragOver: config.onDragOver,
      onDragLeave: config.onDragLeave,
      canDrop: config.canDrop || (() => true)
    };
    
    this.dropZones.set(zoneId, dropConfig);
    
    return {
      'data-drop-zone': zoneId,
      onDragOver: (e) => this.handleDragOver(e, dropConfig),
      onDragLeave: (e) => this.handleDragLeave(e, dropConfig),
      onDrop: (e) => this.handleDrop(e, dropConfig)
    };
  }
  
  /**
   * Handle drag start
   */
  handleDragStart(event, config: Record<string, unknown>): void {
    this.currentDrag = config;
    
    // Set drag data
    event.dataTransfer.effectAllowed = config.exportable ? 'copyMove' : 'move';
    event.dataTransfer.setData('text/plain', config.itemId);
    event.dataTransfer.setData('application/x-draggr', JSON.stringify({
      dragId: config.id,
      itemId: config.itemId,
      type: config.type,
      data: config.data
    }));
    
    // Set custom ghost image if provided
    if (config.ghost) {
      const ghost = document.createElement('div');
      ghost.innerHTML = config.ghost;
      ghost.style.position = 'absolute';
      ghost.style.top = '-1000px';
      document.body.appendChild(ghost);
      event.dataTransfer.setDragImage(ghost, 0, 0);
      setTimeout(() => document.body.removeChild(ghost), 0);
    }
    
    // Handle file export
    if (config.exportable && config.source) {
      this.setupFileExport(event, config);
    }
    
    // Call custom handler
    if (config.onDragStart) {
      config.onDragStart(event, config);
    }
    
    this.notifyListeners('dragStart', { config, event });
  }
  
  /**
   * Handle drag end
   */
  handleDragEnd(event, config: Record<string, unknown>): void {
    this.currentDrag = null;
    
    // Call custom handler
    if (config.onDragEnd) {
      config.onDragEnd(event, config);
    }
    
    this.notifyListeners('dragEnd', { config, event });
  }
  
  /**
   * Handle drag over
   */
  handleDragOver(event, dropConfig): void {
    event.preventDefault();
    
    if (!this.currentDrag) return;
    
    // Check if drop is allowed
    const accepts = dropConfig.accepts;
    const dragType = this.currentDrag.type;
    
    if (!accepts.includes('*') && !accepts.includes(dragType)) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    
    if (!dropConfig.canDrop(this.currentDrag)) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    
    event.dataTransfer.dropEffect = 'move';
    
    // Call custom handler
    if (dropConfig.onDragOver) {
      dropConfig.onDragOver(event, this.currentDrag);
    }
  }
  
  /**
   * Handle drag leave
   */
  handleDragLeave(event, dropConfig): void {
    if (dropConfig.onDragLeave) {
      dropConfig.onDragLeave(event, this.currentDrag);
    }
  }
  
  /**
   * Handle drop
   */
  handleDrop(event, dropConfig): void {
    event.preventDefault();
    
    if (!this.currentDrag) return;
    
    const dragData = this.currentDrag;
    
    // Call drop handler
    if (dropConfig.onDrop) {
      dropConfig.onDrop(event, dragData, dropConfig);
    }
    
    this.notifyListeners('drop', { dragData, dropConfig, event });
  }
  
  /**
   * Setup file export for drag-out
   */
  async setupFileExport(event, config: Record<string, unknown>): Promise<any> {
    if (!config.source || !config.onExport) return;
    
    try {
      // Get file data
      const fileData = await config.onExport();
      
      if (!fileData) return;
      
      // Create download URL
      const blob = fileData.blob || await this.fetchFileBlob(config.source);
      const url = URL.createObjectURL(blob);
      
      // Set download data
      event.dataTransfer.setData('DownloadURL', 
        `${fileData.type}:${fileData.name}:${url}`
      );
      
      this.notifyListeners('fileExport', { config, fileData });
    } catch (error) {
      console.error('File export error:', error);
      this.notifyListeners('fileExportError', { config, error });
    }
  }
  
  /**
   * Fetch file blob from source
   */
  async fetchFileBlob(source): Promise<any> {
    const adapter = this.adapters.get(source.type);
    
    if (!adapter) {
      throw new Error(`No adapter registered for source type: ${source.type}`);
    }
    
    return adapter.fetchBlob(source);
  }
  
  /**
   * Multi-select support
   */
  selectItem(itemId, addToSelection = false): void {
    if (!addToSelection) {
      this.selectedItems.clear();
    }
    this.selectedItems.add(itemId);
    this.notifyListeners('selectionChanged', Array.from(this.selectedItems));
  }
  
  deselectItem(itemId): void {
    this.selectedItems.delete(itemId);
    this.notifyListeners('selectionChanged', Array.from(this.selectedItems));
  }
  
  clearSelection(): void {
    this.selectedItems.clear();
    this.notifyListeners('selectionChanged', []);
  }
  
  getSelectedItems(): any {
    return Array.from(this.selectedItems);
  }
  
  /**
   * Subscribe to events
   */
  subscribe(listener: Function): void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  
  /**
   * Notify listeners
   */
  notifyListeners(event, data: unknown): void {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Draggr listener error:', error);
      }
    });
  }
  
  /**
   * Cleanup
   */
  destroy(): void {
    this.activeDrags.clear();
    this.dropZones.clear();
    this.adapters.clear();
    this.listeners.clear();
    this.selectedItems.clear();
    this.currentDrag = null;
  }
}

export default DraggrManager;
